/**
 * @module engine/coords
 * @summary Screen↔world coordinate transforms and grid snapping.
 * @description
 *   All app logic works in WORLD coordinates (un-zoomed, un-panned). These
 *   helpers convert to/from SCREEN (CSS-pixel) coordinates. Note: the DPR
 *   (high-DPI) scaling is handled separately in the render transform — these
 *   transforms stay in CSS pixels so hit-testing and pointer math are simple.
 * @ported-from JCC PFD Engineering Tool v1.1.html lines 1027-1030.
 */
import { S, GRID } from './state';
import type { Point } from '../types';

/** Screen (CSS px relative to canvas) → world. */
export function toW(sx: number, sy: number): Point {
  return { x: (sx - S.panX) / S.zoom, y: (sy - S.panY) / S.zoom };
}

/** World → screen (CSS px relative to canvas). */
export function toS(wx: number, wy: number): Point {
  return { x: wx * S.zoom + S.panX, y: wy * S.zoom + S.panY };
}

/** Snap a world value to the grid when snapping is enabled. */
export function snap(v: number): number {
  return S.snapOn ? Math.round(v / GRID) * GRID : v;
}

/** The canvas bounding rect (for converting clientX/Y → canvas-local). */
export function getR(): DOMRect {
  return S.canvas
    ? S.canvas.getBoundingClientRect()
    : ({ left: 0, top: 0, width: 0, height: 0 } as DOMRect);
}
